import {FluidGrid} from './physics.js';
const clamp=(value,min,max)=>Math.max(min,Math.min(max,value));
export function sampleField(field,n,x,y){
 const px=clamp(x*n,.5,n-1.5),py=clamp(y*n,.5,n-1.5),ix=Math.floor(px),iy=Math.floor(py),a=px-ix,b=py-iy;
 return (1-b)*((1-a)*field[ix+iy*n]+a*field[ix+1+iy*n])+b*((1-a)*field[ix+(iy+1)*n]+a*field[ix+1+(iy+1)*n]);
}

// A cell mask approximates a circular solid inside the collocated teaching grid.
class MaskedGrid extends FluidGrid{
 constructor(n,solid){super(n);this.solid=solid;}
 boundary(field,axis=0){super.boundary(field,axis);const n=this.n;for(let y=1;y<n-1;y++)for(let x=1;x<n-1;x++)if(this.solid(x,y))field[x+y*n]=0;}
 project(){
  const n=this.n,{u,v,div,pressure:p}=this;p.fill(0);
  this.boundary(u,1);this.boundary(v,2);
  for(let y=1;y<n-1;y++)for(let x=1;x<n-1;x++){
   const i=x+y*n;div[i]=this.solid(x,y)?0:-.5*(u[i+1]-u[i-1]+v[i+n]-v[i-n])/n;
  }
  for(let pass=0;pass<35;pass++){
   for(let y=1;y<n-1;y++)for(let x=1;x<n-1;x++){
    const i=x+y*n;if(this.solid(x,y))continue;
    let sum=0,count=0;if(!this.solid(x-1,y)){sum+=p[i-1];count++;}if(!this.solid(x+1,y)){sum+=p[i+1];count++;}if(!this.solid(x,y-1)){sum+=p[i-n];count++;}if(!this.solid(x,y+1)){sum+=p[i+n];count++;}
    p[i]=count?(div[i]+sum)/count:0;
   }
   super.boundary(p);
  }
  for(let y=1;y<n-1;y++)for(let x=1;x<n-1;x++){
   const i=x+y*n;if(this.solid(x,y))continue;
   const left=this.solid(x-1,y)?p[i]:p[i-1],right=this.solid(x+1,y)?p[i]:p[i+1],up=this.solid(x,y-1)?p[i]:p[i-n],down=this.solid(x,y+1)?p[i]:p[i+n];
   u[i]-=.5*n*(right-left);v[i]-=.5*n*(down-up);
  }
  this.boundary(u,1);this.boundary(v,2);
 }
 advect(out,src,u,v,dt,axis=0){
  const n=this.n;
  for(let y=1;y<n-1;y++)for(let x=1;x<n-1;x++){
   const i=x+y*n;if(this.solid(x,y)){out[i]=0;continue;}
   let px=clamp(x-dt*n*u[i],.5,n-1.5),py=clamp(y-dt*n*v[i],.5,n-1.5);
   const steps=Math.ceil(Math.hypot(px-x,py-y)*2);let lastX=x,lastY=y;
   for(let k=1;k<=steps;k++){const ax=x+(px-x)*k/steps,ay=y+(py-y)*k/steps;if(this.solid(Math.round(ax),Math.round(ay))){px=lastX;py=lastY;break;}lastX=ax;lastY=ay;}
   out[i]=sampleField(src,n,px/n,py/n);
  }
  this.boundary(out,axis);
 }
}

export class FluidObservatory{
 constructor(n=56){this.n=n;this.solidMask=new Uint8Array(n*n);for(let y=0;y<n;y++)for(let x=0;x<n;x++)this.solidMask[x+y*n]=Number(Math.hypot(x/n-.5,y/n-.5)<.105);this.obstacle=true;this.viscosity=.0005;this.source=1;this.time=0;this.preset='jet';this.grid=new MaskedGrid(n,(x,y)=>this.isSolid(x,y));}
 isSolid(x,y){return Boolean(this.obstacle&&this.solidMask[x+y*this.n]);}
 reset(preset=this.preset,warmup=45){
  this.preset=preset;this.time=0;this.grid.reset();const n=this.n;
  for(let y=1;y<n-1;y++)for(let x=1;x<n-1;x++){
   if(this.isSolid(x,y))continue;const i=x+y*n,dx=x/n-.5,dy=y/n-.5;
   if(preset==='vortex'){const w=Math.exp(-(dx*dx+dy*dy)*8);this.grid.u[i]=-dy*w*1.4;this.grid.v[i]=dx*w*1.4;}
   else if(preset==='meeting'){this.grid.u[i]=(x/n<.5?1:-1)*.1;this.grid.v[i]=Math.sin(x/n*Math.PI*2)*.035;}
   else{this.grid.u[i]=.15;this.grid.v[i]=Math.sin(x/n*12)*.015;}
  }
  for(let i=0;i<warmup;i++)this.advance();
 }
 stir(x,y,dx,dy){this.grid.inject(clamp(x,.03,.97),clamp(y,.03,.97),clamp(dx,-.35,.35),clamp(dy,-.35,.35));this.mask();}
 mask(){for(const key of ['u','v','density'])this.grid.boundary(this.grid[key],key==='u'?1:key==='v'?2:0);}
 advance(){
  const g=this.grid,t=this.time,s=this.source;
  if(s>0){
   if(this.preset==='vortex'){g.inject(.3,.5,0,-.045*s);g.inject(.7,.5,0,.045*s);}
   else if(this.preset==='meeting'){g.inject(.16,.39,.055*s,.008*s);g.inject(.84,.61,-.055*s,-.008*s);}
   else g.inject(.13,.5+Math.sin(t*1.8)*.045,.08*s,Math.cos(t*1.8)*.008*s);
  }
  this.mask();g.step(1/30,this.viscosity);this.time+=1/30;
 }
 metrics(){
  const g=this.grid,n=this.n;let energy=0,maxSpeed=0,dye=0,count=0;
  for(let y=1;y<n-1;y++)for(let x=1;x<n-1;x++){if(this.isSolid(x,y))continue;const i=x+y*n,speed=Math.hypot(g.u[i],g.v[i]);energy+=speed*speed;maxSpeed=Math.max(maxSpeed,speed);dye+=g.density[i];count++;}
  return {energy:energy/count,maxSpeed,dye:dye/count,divergence:g.divergence()};
 }
}
